import { useState, useMemo, useRef, type ReactNode } from "react";
import { randomId } from "@/shared/utils/randomId";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DatePicker } from "@/components/ui/date-picker";
import { useRecordSowing, useGerminationChambers } from "../../hooks/useSowingInstructions";
import { useSowingsByInstruction } from "../../hooks/useSowingLifecycle";
import { useUnitsOfMeasure } from "@/shared/hooks/useUnitsOfMeasure";
import { useNotification } from "@/shared/hooks/useNotification";
import { useConfirm } from "@/shared/contexts/confirm-context";
import { SowingConfirmBody } from "./SowingConfirmSummary";
import { useRequisitionsForSowing } from "@/features/inventory/hooks/useRequisition";
import {
  RequisitionStatus,
  getStatusLabel as getRequisitionStatusLabel,
  type Requisition,
} from "@/features/inventory/services/requisitionApi";
import { Badge } from "@/components/ui/badge";
import type { SowingInstruction } from "../../services/sowingInstructionsApi";
import { getErrorMessage } from "@/shared/utils/errorHandler";

interface RecordSowingDialogProps {
  instruction: SowingInstruction;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const RecordSowingDialog = ({
  instruction,
  open,
  onOpenChange,
  onSuccess,
}: RecordSowingDialogProps) => {
  const [formData, setFormData] = useState({
    actualSowingDate: new Date().toISOString().split("T")[0],
    quantitySown: "",
    quantitySownUnitOfMeasure: instruction.unit.toString(),
    seedsUsedQuantity: "",
    seedsUsedUnitOfMeasure: instruction.unit.toString(),
    seedsReturnedQuantity: "",
    seedsReturnedUnitOfMeasure: instruction.unit.toString(),
    chamberPlacements: [] as { chamberId: string; trays: string }[],
    notes: "",
  });

  const recordMutation = useRecordSowing();
  const submitKey = useRef(randomId());
  const notify = useNotification();
  const confirm = useConfirm();
  const unitsQuery = useUnitsOfMeasure();
  const chambersQuery = useGerminationChambers();
  // Partial sowings accumulate one requisition per batch, so fetch the full set
  // and pick which one applies to THIS batch.
  const requisitionsQuery = useRequisitionsForSowing(open ? instruction.id : undefined);
  const sowingsQuery = useSowingsByInstruction(open ? instruction.id : "");
  const [selectedRequisitionId, setSelectedRequisitionId] = useState<string>("");

  // Per-requisition-item used/returned breakdown — keyed by RequisitionItem id.
  const [itemUsages, setItemUsages] = useState<
    Record<string, { used: string; returned: string }>
  >({});

  // RequisitionItem.unitOfMeasure can arrive as either the enum's name ("Grams")
  // or its numeric value ("2") depending on the endpoint's serializer config.
  // resolveUnit handles both so we get the canonical meta for display + payload.
  const resolveUnit = (raw: string | undefined) => {
    if (!raw) return undefined;
    const s = String(raw).toLowerCase();
    return unitsQuery.data?.find(
      (u) =>
        String(u.value) === String(raw) ||
        u.name.toLowerCase() === s ||
        u.abbreviation?.toLowerCase() === s
    );
  };
  const formatUnit = (raw: string | undefined) => {
    const meta = resolveUnit(raw);
    return meta?.abbreviation || meta?.name || raw || "";
  };
  const toUnitValue = (raw: string | undefined): number | null => {
    const meta = resolveUnit(raw);
    if (meta) return meta.value;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  };

  // Compute requisitions eligible for THIS batch: in Issued/PartiallyIssued status
  // AND not already linked to an existing Sowing row (each requisition can only back
  // one batch, per SowingLifecycleService.CreateSowingAsync's already-linked check).
  const allRequisitions = useMemo(() => requisitionsQuery.data ?? [], [requisitionsQuery.data]);
  const usedRequisitionIds = useMemo(
    () =>
      new Set(
        (sowingsQuery.data ?? [])
          .map((s) => s.requisitionId)
          .filter((id): id is string => !!id)
      ),
    [sowingsQuery.data]
  );

  const eligibleRequisitions = useMemo(
    () =>
      allRequisitions.filter((r) => {
        const status = r.requisitionStatus ?? r.status;
        const isReady =
          status === RequisitionStatus.Issued ||
          status === RequisitionStatus.PartiallyIssued;
        return isReady && !usedRequisitionIds.has(r.id);
      }),
    [allRequisitions, usedRequisitionIds]
  );

  // Keep a valid explicit selection; otherwise derive the newest eligible
  // requisition without synchronizing state from an effect.
  const effectiveRequisitionId = eligibleRequisitions.some(
    (requisition) => requisition.id === selectedRequisitionId
  )
    ? selectedRequisitionId
    : eligibleRequisitions[0]?.id ?? "";

  const linkedRequisition = useMemo<Requisition | null>(
    () =>
      allRequisitions.find((r) => r.id === effectiveRequisitionId) ?? null,
    [allRequisitions, effectiveRequisitionId]
  );

  const linkedItems = useMemo<Array<{
    id: string;
    inputName?: string;
    quantityIssued?: number;
    unitOfMeasure?: string;
    seedBatchId?: string;
    isExternalItem?: boolean;
  }>>(
    () =>
      (linkedRequisition?.items ?? [])
        .filter(
          (it) =>
            !!it.id &&
            it.sowingInstructionId === instruction.id &&
            (it.quantityIssued ?? 0) > 0
        )
        .map((it) => ({
          id: it.id as string,
          inputName: it.inputName,
          quantityIssued: it.quantityIssued,
          unitOfMeasure: it.unitOfMeasure,
          seedBatchId: it.seedBatchId ?? undefined,
          isExternalItem: it.isExternalItem,
        })),
    [linkedRequisition, instruction.id]
  );

  const hasMultiItemRequisition = linkedItems.length > 0;

  // Whether a usable requisition is selected — gates Record Sowing per backend rule.
  const hasRequisition = !!linkedRequisition && (linkedRequisition.items ?? []).some(
    (it) => it.sowingInstructionId === instruction.id
  );
  const requisitionStatusValue =
    linkedRequisition?.requisitionStatus ?? linkedRequisition?.status;
  const requisitionStatusLabel =
    typeof requisitionStatusValue === "number"
      ? getRequisitionStatusLabel(requisitionStatusValue)
      : "Unknown";
  const isRequisitionIssued =
    requisitionStatusValue === RequisitionStatus.Issued ||
    requisitionStatusValue === RequisitionStatus.PartiallyIssued;

  // Customer-seed sowings need at least one seed-batch row. A customer may provide
  // multiple packets/batches for the same instruction, so don't require the original
  // instruction.seedBatchId specifically.
  const customerSeedBatchId = instruction.seedBatchId;
  const customerSeedRowMissing =
    !!customerSeedBatchId &&
    !(linkedRequisition?.items ?? []).some(
      (it) => !!it.seedBatchId
    );

  const requisitionGateBlocked =
    !requisitionsQuery.isLoading &&
    (!hasRequisition || customerSeedRowMissing);

  // Inline guard: if any per-item used + returned exceeds issued, block submit
  // and surface red borders + an inline error row in the table.
  const hasItemOvershoot = hasMultiItemRequisition
    ? linkedItems.some((it) => {
        const u = itemUsages[it.id];
        const used = u ? parseFloat(u.used || "0") || 0 : 0;
        const ret = u ? parseFloat(u.returned || "0") || 0 : 0;
        return used + ret > (it.quantityIssued ?? 0) + 1e-9;
      })
    : false;

  const updateUsage = (itemId: string, field: "used" | "returned", value: string) =>
    setItemUsages((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], [field]: value, ...(prev[itemId] ? {} : { used: "", returned: "" }) },
    }));

  const handleSubmit = () => {
    if (!formData.actualSowingDate || !formData.quantitySown) {
      notify.error("Please fill in all required fields");
      return;
    }

    // Mirror the backend rule: every sowing needs a requisition first.
    if (!hasRequisition) {
      notify.error(
        "Raise a requisition for this sowing before recording it. Use Create Requisition from the row's actions menu."
      );
      return;
    }
    if (customerSeedRowMissing) {
      notify.error(
        "Customer seed batch is missing from the requisition. Add the seed batch row before recording sowing."
      );
      return;
    }
    if (customerSeedBatchId && !linkedItems.some((it) =>
      !!it.seedBatchId && Number(itemUsages[it.id]?.used || 0) > 0)) {
      notify.error("Enter the actual seed weight used from an issued customer seed batch.");
      return;
    }

    // Validate per-item used+returned <= issued for the multi-item requisition flow.
    if (hasMultiItemRequisition) {
      for (const it of linkedItems) {
        const u = itemUsages[it.id];
        const used = u ? parseFloat(u.used || "0") : 0;
        const ret = u ? parseFloat(u.returned || "0") : 0;
        const issued = it.quantityIssued ?? 0;
        if (used < 0 || ret < 0) {
          notify.error(`Used and returned must be non-negative for ${it.inputName}`);
          return;
        }
        if (used + ret > issued + 1e-9) {
          notify.error(
            `${it.inputName}: used (${used}) + returned (${ret}) exceeds issued (${issued})`
          );
          return;
        }
      }
    }

    let itemUsagesPayload: Array<{
      requisitionItemId: string;
      quantityUsed: number;
      quantityReturned: number;
      unitOfMeasure: number;
    }> | undefined;
    if (hasMultiItemRequisition) {
      const rows = [];
      for (const it of linkedItems) {
        const u = itemUsages[it.id];
        const used = u ? parseFloat(u.used || "0") : 0;
        const returned = u ? parseFloat(u.returned || "0") : 0;
        if (used <= 0 && returned <= 0) continue;
        // Each item's return is in the *input's* unit (what it was issued in),
        // not the sowing's overall unit. The backend rejects cross-category
        // unit mismatches (e.g. Trays returned against a Grams input).
        const unitValue = toUnitValue(it.unitOfMeasure);
        if (unitValue == null) {
          notify.error(
            `Couldn't resolve the unit for ${it.inputName}. Reopen the dialog after units load.`
          );
          return;
        }
        rows.push({
          requisitionItemId: it.id,
          quantityUsed: used,
          quantityReturned: returned,
          unitOfMeasure: unitValue,
        });
      }
      itemUsagesPayload = rows;
    }

    confirm({
      title: "Record sowing?",
      description: "The sown quantity will be recorded for this instruction.",
      confirmLabel: "Record sowing",
      body: SowingConfirmBody(instruction, [
        { label: "Sowing date", value: formData.actualSowingDate },
        { label: "Quantity sown", value: formData.quantitySown },
      ]),
      onConfirm: () =>
        recordMutation.mutateAsync(
      {
        sowingInstructionId: instruction.id,
        idempotencyKey: submitKey.current,
        actualSowingDate: new Date(formData.actualSowingDate).toISOString(),
        quantitySown: parseFloat(formData.quantitySown),
        quantitySownUnitOfMeasure: parseInt(formData.quantitySownUnitOfMeasure),
        // Legacy single-pair fields are skipped when ItemUsages is present (server enforces mutual exclusion).
        seedsUsedQuantity: hasMultiItemRequisition
          ? undefined
          : formData.seedsUsedQuantity ? parseFloat(formData.seedsUsedQuantity) : undefined,
        seedsUsedUnitOfMeasure: hasMultiItemRequisition
          ? undefined
          : formData.seedsUsedQuantity ? parseInt(formData.seedsUsedUnitOfMeasure) : undefined,
        seedsReturnedQuantity: hasMultiItemRequisition
          ? undefined
          : formData.seedsReturnedQuantity ? parseFloat(formData.seedsReturnedQuantity) : undefined,
        seedsReturnedUnitOfMeasure: hasMultiItemRequisition
          ? undefined
          : formData.seedsReturnedQuantity ? parseInt(formData.seedsReturnedUnitOfMeasure) : undefined,
        chamberPlacements: formData.chamberPlacements
          .filter((p) => p.chamberId && parseFloat(p.trays) > 0)
          .map((p) => ({
            germinationChamberId: p.chamberId,
            traysPlaced: parseFloat(p.trays),
          })),
        itemUsages: itemUsagesPayload,
        notes: formData.notes,
      })
          .then(() => {
            submitKey.current = randomId();
            onSuccess();
          })
          .catch((error: unknown) => {
            notify.error(getErrorMessage(error) || "Failed to record sowing");
            throw error;
          }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto overflow-x-hidden w-[95vw] sm:w-[800px] sm:max-w-[800px] max-w-[95vw]">
        <DialogHeader>
          <DialogTitle>Record Sowing</DialogTitle>
          <DialogDescription>
            {(() => {
              const unitName =
                unitsQuery.data?.find((u) => u.value === instruction.unit)?.name;
              const unitLabel = unitName ?? "units";
              const cropLabel = instruction.cropTypeName?.trim();
              const variety = instruction.variety?.trim();
              const subject = [cropLabel, variety].filter(Boolean).join(" — ");
              return `${subject || "Sowing"} · ${instruction.quantityToSow} ${unitLabel}`;
            })()}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Requisition gate — mirrors the backend RecordSowing precondition. */}
          {requisitionsQuery.isLoading ? (
            <div className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
              Checking requisition status…
            </div>
          ) : eligibleRequisitions.length === 0 ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm space-y-1">
              <div className="font-medium text-red-800">
                ⚠️ No eligible requisition for this sowing batch
              </div>
              <p className="text-red-700">
                {allRequisitions.length === 0
                  ? "No requisition has been raised yet. Close this dialog and use Create Requisition from the row's actions menu."
                  : "All existing requisitions for this sowing instruction are already linked to earlier batches or not yet issued. Close this dialog and raise a new requisition for this batch via Create Requisition from the row's actions menu."}
              </p>
            </div>
          ) : customerSeedRowMissing ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm space-y-1">
              <div className="font-medium text-amber-900">
                🌱 Customer seed batch missing from requisition
              </div>
              <p className="text-amber-800">
                This sowing uses customer seeds. The selected requisition (
                {linkedRequisition?.requisitionNumber ?? "—"}) doesn't include
                a customer seed batch row. Update the requisition before
                recording sowing.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {eligibleRequisitions.length > 1 ? (
                <Field>
                  <FieldLabel>
                    Requisition for this batch
                  </FieldLabel>
                  <Select
                    value={effectiveRequisitionId}
                    onValueChange={setSelectedRequisitionId}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {eligibleRequisitions.map((r) => {
                        const s = r.requisitionStatus ?? r.status;
                        return (
                          <SelectItem key={r.id} value={r.id}>
                            {r.requisitionNumber} — {getRequisitionStatusLabel(s)}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {eligibleRequisitions.length} requisitions are eligible (issued and not yet linked to a sowing batch). Each requisition backs one batch.
                  </p>
                </Field>
              ) : (
                <div className="rounded-md border bg-muted/30 p-3 text-xs flex items-center justify-between">
                  <span className="text-muted-foreground">
                    Linked requisition{" "}
                    <span className="font-medium text-foreground">
                      {linkedRequisition?.requisitionNumber ?? "—"}
                    </span>
                  </span>
                  <Badge variant={isRequisitionIssued ? "default" : "secondary"}>
                    {requisitionStatusLabel}
                  </Badge>
                </div>
              )}
            </div>
          )}

          <Field>
            <FieldLabel htmlFor="actualSowingDate">
              Actual Sowing Date <span className="text-red-500">*</span>
            </FieldLabel>
            <DatePicker
              id="actualSowingDate"
              value={formData.actualSowingDate}
              onChange={(value) =>
                setFormData((prev) => ({ ...prev, actualSowingDate: value }))
              }
              disabled={recordMutation.isPending}
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="quantitySown">
              Quantity Sown <span className="text-red-500">*</span>
            </FieldLabel>
            <Input
              id="quantitySown"
              type="number"
              step="0.01"
              placeholder="0.00"
              value={formData.quantitySown}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  quantitySown: e.target.value,
                }))
              }
              disabled={recordMutation.isPending}
            />
            {(() => {
              const alreadySown = Number(instruction.quantitySown ?? 0);
              const target = Number(instruction.quantityToSow ?? 0);
              if (alreadySown <= 0) return null;
              const remaining = Math.max(target - alreadySown, 0);
              const unitName =
                unitsQuery.data?.find((u) => u.value === instruction.unit)?.name?.toLowerCase() ??
                "units";
              return (
                <p className="mt-1 text-xs text-muted-foreground">
                  Already sown: <span className="font-medium text-foreground">{alreadySown}</span> /{" "}
                  {target} {unitName} · {remaining} {unitName} remaining
                </p>
              );
            })()}
          </Field>

          <Field>
            <FieldLabel htmlFor="quantitySownUnit">Unit</FieldLabel>
            <Select
              value={formData.quantitySownUnitOfMeasure}
              onValueChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  quantitySownUnitOfMeasure: value,
                }))
              }
              disabled={recordMutation.isPending}
            >
              <SelectTrigger id="quantitySownUnit">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {unitsQuery.data?.map((unit) => (
                  <SelectItem key={unit.value} value={unit.value.toString()}>
                    {unit.name} ({unit.abbreviation})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <div className="border-t pt-4">
            {hasMultiItemRequisition ? (
              <>
                <p className="text-sm font-semibold mb-3">
                  Per-Item Usage & Returns
                </p>
                <p className="text-xs text-muted-foreground mb-3">
                  Linked requisition {linkedRequisition?.requisitionNumber}.
                  Enter how much of each requisitioned item was used and how much
                  is being returned to the store. Returns post immediately as
                  stock Return movements.
                </p>
                <div className="rounded border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead className="w-16 text-right">Issued</TableHead>
                        <TableHead className="w-32">Used</TableHead>
                        <TableHead className="w-32">Returned</TableHead>
                        <TableHead className="w-12">Unit</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {linkedItems.flatMap((it) => {
                        const u = itemUsages[it.id] ?? { used: "", returned: "" };
                        const issued = it.quantityIssued ?? 0;
                        const hasInput = u.used !== "" || u.returned !== "";
                        const usedNum = parseFloat(u.used) || 0;
                        const returnedNum = parseFloat(u.returned) || 0;
                        const total = usedNum + returnedNum;
                        const overshoot = total > issued + 1e-9;
                        const overshootBy = overshoot ? total - issued : 0;
                        // Undershoot: caller filled at least one of the fields, but total
                        // still doesn't match issued — flag so seeds aren't silently lost.
                        const undershoot = !overshoot && hasInput && total + 1e-9 < issued;
                        const undershootBy = undershoot ? issued - total : 0;

                        // Return a flat array of TableRow elements. Wrapping in a fragment
                        // here breaks <tbody>'s child-element invariant when a conditional
                        // row is later spliced in, which causes the browser to misnest cells
                        // and visually collapse columns.
                        const rows: ReactNode[] = [
                          <TableRow key={it.id}>
                            <TableCell className="text-sm">
                              {it.inputName}
                              {it.isExternalItem && (
                                <span className="ml-2 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-700">
                                  External
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-right">
                              {issued}
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="0"
                                value={u.used}
                                onChange={(e) =>
                                  updateUsage(it.id, "used", e.target.value)
                                }
                                disabled={recordMutation.isPending}
                                className={
                                  overshoot
                                    ? "border-red-400 focus-visible:ring-red-400"
                                    : undershoot
                                    ? "border-amber-400 focus-visible:ring-amber-400"
                                    : undefined
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="0"
                                value={u.returned}
                                onChange={(e) =>
                                  updateUsage(it.id, "returned", e.target.value)
                                }
                                disabled={recordMutation.isPending}
                                className={
                                  overshoot
                                    ? "border-red-400 focus-visible:ring-red-400"
                                    : undershoot
                                    ? "border-amber-400 focus-visible:ring-amber-400"
                                    : undefined
                                }
                              />
                            </TableCell>
                            <TableCell className="text-xs">
                              {formatUnit(it.unitOfMeasure)}
                            </TableCell>
                          </TableRow>,
                        ];
                        if (overshoot) {
                          rows.push(
                            <TableRow key={`${it.id}-error`} className="bg-red-50 hover:bg-red-50">
                              <TableCell colSpan={5} className="text-xs text-red-700 py-1.5 whitespace-normal break-words">
                                ⚠️ Used + returned ({usedNum} + {returnedNum} ={" "}
                                {total.toFixed(2)}) exceeds issued ({issued}) by{" "}
                                {overshootBy.toFixed(2)} {formatUnit(it.unitOfMeasure)}.
                              </TableCell>
                            </TableRow>
                          );
                        }
                        if (undershoot) {
                          rows.push(
                            <TableRow key={`${it.id}-warn`} className="bg-amber-50 hover:bg-amber-50">
                              <TableCell colSpan={5} className="text-xs text-amber-800 py-1.5 whitespace-normal break-words">
                                ⚠ {undershootBy.toFixed(2)} {formatUnit(it.unitOfMeasure)} unaccounted —
                                used + returned ({total.toFixed(2)}) is less than issued ({issued}).
                                Acceptable only if more sowings will draw on the same issuance.
                              </TableCell>
                            </TableRow>
                          );
                        }
                        return rows;
                      })}
                    </TableBody>
                  </Table>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm font-semibold mb-3">Seeds Used & Returned</p>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
                  <Field>
                    <FieldLabel htmlFor="seedsUsedQuantity">
                      Seeds Used Quantity
                    </FieldLabel>
                    <Input
                      id="seedsUsedQuantity"
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={formData.seedsUsedQuantity}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          seedsUsedQuantity: e.target.value,
                        }))
                      }
                      disabled={recordMutation.isPending}
                    />
                  </Field>

                  <Field>
                    <FieldLabel htmlFor="seedsReturnedQuantity">
                      Seeds Returned Quantity
                    </FieldLabel>
                    <Input
                      id="seedsReturnedQuantity"
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={formData.seedsReturnedQuantity}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          seedsReturnedQuantity: e.target.value,
                        }))
                      }
                      disabled={recordMutation.isPending}
                    />
                  </Field>
                </div>
              </>
            )}
          </div>

          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold">Chamber Placements (optional)</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    chamberPlacements: [
                      ...prev.chamberPlacements,
                      { chamberId: "", trays: "" },
                    ],
                  }))
                }
                disabled={recordMutation.isPending}
              >
                + Add Chamber
              </Button>
            </div>
            {formData.chamberPlacements.length > 0 && (
              <div className="space-y-2">
                {formData.chamberPlacements.map((placement, idx) => (
                  <div key={idx} className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Select
                        value={placement.chamberId}
                        onValueChange={(value) => {
                          const updated = [...formData.chamberPlacements];
                          updated[idx] = { ...updated[idx], chamberId: value };
                          setFormData((prev) => ({
                            ...prev,
                            chamberPlacements: updated,
                          }));
                        }}
                        disabled={recordMutation.isPending}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select chamber" />
                        </SelectTrigger>
                        <SelectContent>
                          {chambersQuery.data
                            ?.filter((c) => c.isActive)
                            .map((chamber) => (
                              <SelectItem key={chamber.id} value={chamber.id}>
                                {chamber.chamberName} ({chamber.chamberCode})
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-28">
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Trays"
                        value={placement.trays}
                        onChange={(e) => {
                          const updated = [...formData.chamberPlacements];
                          updated[idx] = {
                            ...updated[idx],
                            trays: e.target.value,
                          };
                          setFormData((prev) => ({
                            ...prev,
                            chamberPlacements: updated,
                          }));
                        }}
                        disabled={recordMutation.isPending}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const updated = formData.chamberPlacements.filter(
                          (_, i) => i !== idx
                        );
                        setFormData((prev) => ({
                          ...prev,
                          chamberPlacements: updated,
                        }));
                      }}
                      disabled={recordMutation.isPending}
                    >
                      ✕
                    </Button>
                  </div>
                ))}
                {formData.quantitySown && (
                  <p className="text-xs text-gray-500">
                    Assigned:{" "}
                    {formData.chamberPlacements
                      .reduce((sum, p) => sum + (parseFloat(p.trays) || 0), 0)
                      .toFixed(2)}{" "}
                    / {formData.quantitySown} trays
                  </p>
                )}
              </div>
            )}
          </div>

          <Field>
            <FieldLabel htmlFor="notes">Notes</FieldLabel>
            <Textarea
              id="notes"
              placeholder="Any additional notes..."
              value={formData.notes}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, notes: e.target.value }))
              }
              disabled={recordMutation.isPending}
            />
          </Field>

          <div className="flex gap-2 justify-end">
            <Button
              variant="secondary"
              onClick={() => onOpenChange(false)}
              disabled={recordMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                recordMutation.isPending || requisitionGateBlocked || hasItemOvershoot
              }
            >
              {recordMutation.isPending ? "Recording..." : "Record Sowing"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
